/*
  This class creates and manages a Three.js canvas with interactive media (video)
  textures. It handles the camera, scene, renderer, pointer input, the render
  loop and resizing logic. The class also renders the media using a
  Media class instance, which applies custom shaders and deformations to
  the video element.
*/

import * as THREE from 'three'
import { Dimensions, Size } from './types'
import ShowreelMedia from './ShowreelMedia'

const MAX_PIXEL_RATIO = 2

export default class ShowreelCanvas {
  element: HTMLCanvasElement        // The HTML canvas element where the Three.js scene is rendered
  scene: THREE.Scene                // The Three.js scene containing all objects and lights
  camera!: THREE.PerspectiveCamera  // Perspective camera for the scene
  renderer!: THREE.WebGLRenderer    // The renderer for displaying the scene on the canvas
  sizes!: Size                      // Dimensions of the media plane
  dimensions!: Dimensions           // Dimensions of the window (for resizing and pixel ratio)
  clock: THREE.Clock                // Clock used for managing time-based rendering updates
  media: ShowreelMedia              // Media instance that manages the video texture and interaction
  private animationFrameId = 0

  constructor(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
    this.element = canvas
    this.clock = new THREE.Clock()
    this.scene = new THREE.Scene()

    this.createCamera()
    this.createRenderer()
    this.setSizes()
    this.media = new ShowreelMedia({
      element: video,
      scene: this.scene,
      sizes: this.sizes,
      renderer: this.renderer,
    })
    this.render()
  }

  // Create and position the camera in the scene
  createCamera() {
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100)
    this.scene.add(this.camera)
    this.camera.position.z = 10
  }

  // Set up the WebGL renderer and adjust its settings
  createRenderer() {
    this.dimensions = {
      width: window.innerWidth,
      height: window.innerHeight,
      pixelRatio: Math.min(MAX_PIXEL_RATIO, window.devicePixelRatio),
    }

    // Create the WebGLRenderer with transparency
    this.renderer = new THREE.WebGLRenderer({ canvas: this.element, alpha: true })
    this.renderer.setPixelRatio(this.dimensions.pixelRatio)
    this.renderer.setSize(this.dimensions.width, this.dimensions.height)
  }

  // Size of the camera's view at the plane's depth, so a plane of these dimensions fills the view
  setSizes() {
    const fovRadians = this.camera.fov * (Math.PI / 180)
    const height = this.camera.position.z * Math.tan(fovRadians / 2) * 2
    const width = height * this.camera.aspect

    this.sizes = {
      width: width,
      height: height,
    }
  }

  // uv has (0,0) at the bottom left, while window coordinates put (0,0) at the top left, so the y-axis is inverted.
  onPointerMove = (event: MouseEvent | TouchEvent) => {
    const point = 'touches' in event ? event.touches[0] : event
    if (!point) return

    const u = point.clientX / window.innerWidth
    const v = 1.0 - point.clientY / window.innerHeight
    this.media.onPointerMove(new THREE.Vector2(u, v))
  }

  // Handle window resize events and adjust the canvas size, camera, and media dimensions
  onResize = () => {
    this.dimensions = {
      width: window.innerWidth,
      height: window.innerHeight,
      pixelRatio: Math.min(MAX_PIXEL_RATIO, window.devicePixelRatio),
    }

    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.setSizes()

    this.renderer.setPixelRatio(this.dimensions.pixelRatio)
    this.renderer.setSize(this.dimensions.width, this.dimensions.height)

    this.media.onResize(this.sizes)
  }

  // Resume rendering and input; the slide calls this each time it becomes active
  start() {
    window.addEventListener('mousemove', this.onPointerMove)
    window.addEventListener('touchmove', this.onPointerMove)
    window.addEventListener('resize', this.onResize)
    this.onResize()
    if (!this.animationFrameId) this.loop()
  }

  stop() {
    window.removeEventListener('mousemove', this.onPointerMove)
    window.removeEventListener('touchmove', this.onPointerMove)
    window.removeEventListener('resize', this.onResize)
    cancelAnimationFrame(this.animationFrameId)
    this.animationFrameId = 0
  }

  private loop = () => {
    this.render()
    this.animationFrameId = requestAnimationFrame(this.loop)
  }

  // Draw one frame of the scene
  render() {
    const dt = this.clock.getDelta()

    this.media.render(dt)

    this.renderer.render(this.scene, this.camera)
  }

  dispose() {
    this.stop()
    this.media.dispose()
    this.renderer.dispose()
  }
}
